// src/components/shell/Sidebar/SidebarModeList.tsx
// List of modes inside the currently active bucket. Active mode gets a
// highlight color per doctrine answer A2. Rendered only on rail/desktop —
// mobile uses MobileBottomTabBar for the same data.
import { useAuth } from "@/contexts/AuthContext";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import {
  MODE_GLYPH,
  getAccessibleModesInBucket,
  type PrimaryMode,
} from "@/components/shell/modes/modesRegistry";
import type { BucketId } from "@/components/shell/modes/bucketsRegistry";
import { ModeUrgencyBadge } from "@/components/shell/ModeUrgencyBadge";
import {
  FIREFLY_TARGET_ATTR,
  fireflyModeNavTargetId,
} from "@/lib/firefly/fireflyTargetConvention";

export type SidebarModeListProps = {
  expanded: boolean;
  mode: PrimaryMode;
  setMode: (m: PrimaryMode) => void;
  activeBucketId: BucketId | null;
  onSelect?: () => void;
};

export function SidebarModeList({
  expanded,
  mode,
  setMode,
  activeBucketId,
  onSelect,
}: SidebarModeListProps) {
  const auth = useAuth();
  const { anchors } = useAnchorEntitlements();
  if (!activeBucketId) return null;
  const accessible = getAccessibleModesInBucket(activeBucketId, auth, anchors);

  return (
    <nav aria-label="Workspace modes" className="px-2 py-2">
      <ul className="space-y-1">
        {accessible.map((entry) => {
          const isActive = entry.id === mode;
          return (
            <li key={entry.id}>
              <button
                type="button"
                {...{ [FIREFLY_TARGET_ATTR]: fireflyModeNavTargetId(entry.id) }}
                onClick={() => {
                  setMode(entry.id);
                  onSelect?.();
                }}
                aria-current={isActive ? "page" : undefined}
                title={expanded ? undefined : entry.label}
                className={[
                  "w-full flex items-center gap-3 rounded-xl px-2.5 py-2 text-left text-sm transition-colors",
                  isActive
                    ? "bg-indigo-50 text-indigo-900 font-semibold dark:bg-indigo-950/40 dark:text-indigo-100"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800/70",
                ].join(" ")}
              >
                <span
                  className={[
                    "relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold tracking-tight",
                    isActive
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
                  ].join(" ")}
                >
                  <span aria-hidden>{MODE_GLYPH[entry.id]}</span>
                  <ModeUrgencyBadge mode={entry.id} currentMode={mode} size="rail" />
                </span>
                {expanded ? <span className="truncate">{entry.label}</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
